import { api } from './api'

export interface AdminUser {
  _id: string
  email: string
  firstName: string
  lastName?: string
  phone?: string
  role: 'SUPPORT_ADMIN' | 'SUPER_ADMIN'
  permissions: string[]
  isActive: boolean
  emailVerified: boolean
  lastLogin?: string
  createdAt: string
  updatedAt: string
}

export interface AdminPermissionDef {
  key: string
  label: string
  group: string
}

export interface CreateAdminPayload {
  email: string
  password: string
  firstName: string
  lastName?: string
  phone?: string
  role?: 'SUPPORT_ADMIN' | 'SUPER_ADMIN'
  permissions?: string[]
}

export interface ListAdminsParams {
  search?: string
  role?: 'SUPPORT_ADMIN' | 'SUPER_ADMIN'
  isActive?: 'true' | 'false'
}

const unwrap = <T,>(data: any, key: string): T => {
  if (!data || typeof data !== 'object') {
    throw new Error('Unexpected response shape')
  }
  return data[key]
}

class AdminUsersService {
  async listAdmins(params: ListAdminsParams = {}): Promise<AdminUser[]> {
    const qs = new URLSearchParams()
    if (params.search) qs.append('search', params.search)
    if (params.role) qs.append('role', params.role)
    if (params.isActive) qs.append('isActive', params.isActive)
    const query = qs.toString()
    const res = await api.get(`/admins${query ? `?${query}` : ''}`)
    return unwrap<AdminUser[]>(res.data?.data, 'admins') ?? []
  }

  async listPermissions(): Promise<{
    permissions: AdminPermissionDef[]
    defaultSupportAdminPermissions: string[]
  }> {
    const res = await api.get('/admins/permissions')
    const data = res.data?.data ?? {}
    return {
      permissions: data.permissions ?? [],
      defaultSupportAdminPermissions: data.defaultSupportAdminPermissions ?? [],
    }
  }

  async createAdmin(payload: CreateAdminPayload): Promise<AdminUser> {
    const res = await api.post('/admins', payload)
    return unwrap<AdminUser>(res.data?.data, 'admin')
  }

  async updatePermissions(id: string, permissions: string[]): Promise<AdminUser> {
    const res = await api.put(`/admins/${id}/permissions`, { permissions })
    return unwrap<AdminUser>(res.data?.data, 'admin')
  }

  async toggleStatus(id: string, isActive: boolean): Promise<AdminUser> {
    const res = await api.patch(`/admins/${id}/status`, { isActive })
    return unwrap<AdminUser>(res.data?.data, 'admin')
  }

  async deleteAdmin(id: string): Promise<void> {
    await api.delete(`/admins/${id}`)
  }
}

export const adminUsersService = new AdminUsersService()
